// src/services/signalGroupCrypto.js
//
// Chiffrement "de groupe" pour les données de discussion (titre, description)
// dans les discussions PRIVÉES.
//
// Principe :
// - Une clé AES-256 symétrique est générée une fois par discussion ("clé de groupe").
// - Le titre/description sont chiffrés avec cette clé (AES-GCM classique).
// - La clé de groupe elle-même est "enveloppée" (wrap) séparément pour chaque
//   participant : soit via selfEncryptKey (pour le propriétaire du wrap lui-même),
//   soit via une session Signal pairwise (pour envelopper à destination d'un autre
//   participant, exactement comme keyForRecipient/keyForSender pour les messages).
// - Un participant qui veut lire le titre récupère SON wrap, le déchiffre, obtient
//   la clé de groupe en clair, puis déchiffre title/description avec.
//
// Ce module ne touche jamais au réseau : il prend/retourne des objets JS que
// l'appelant (Discussion.js, le flux d'invitation, etc.) se charge d'envoyer/lire
// via l'API.

import { SignalProtocolAddress, SessionCipher } from '@privacyresearch/libsignal-protocol-typescript';
import { signalStore, cacheKeyOf } from './signalStore';
import {
  aesEncryptBytes, 
  aesDecryptBytes,
  selfEncryptKey,
  selfDecryptKey,
  bufferToBase64,
  base64ToBuffer,
} from './signalCrypto';
import { buildSessionWithContact } from './signalSession';
import {
  encryptKeyForUser,
  decryptKeyFromUser,
} from './signalSession';

import {
  getCachedGroupMessageKey,
  setCachedGroupMessageKey,
} from './signalStore';

import { withSignalLock } from './signalSession'; 
import api from './api';

const GROUP_KEY_CACHE_DB = 'group-key-cache';
const GROUP_KEY_CACHE_STORE = 'keys';

async function persistSelfWrap(discussionId, groupKeyRaw) {
  try {
    const selfWrap = await wrapGroupKeyForSelf(groupKeyRaw);
    await api.put(`/api/discussions/${discussionId}/groupkeywrap/self`, {
      encryptedKey: selfWrap.encryptedKey,
      keyIv: selfWrap.keyIv,
    });
  } catch (err) {
    // Non bloquant : la clé reste utilisable, on réessaiera à la prochaine ouverture
    console.error('⚠️ Sauvegarde du wrap self impossible :', discussionId, err);
  }
}

export function openGroupKeyCacheDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(GROUP_KEY_CACHE_DB, 2);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(GROUP_KEY_CACHE_STORE)) {
        req.result.createObjectStore(GROUP_KEY_CACHE_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

//réparation déchiffrement
async function deleteCachedGroupKey(discussionId) {
  try {
    const db = await openGroupKeyCacheDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(GROUP_KEY_CACHE_STORE, 'readwrite');
      tx.objectStore(GROUP_KEY_CACHE_STORE).delete(discussionId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.error('Erreur suppression cache clé de groupe:', err);
  }
}


/**
 * Ré-enveloppe la clé de groupe pour un participant dont l'identité Signal
 * a changé (ex : après une récupération par phrase ayant perdu ses sessions).
 * Force la reconstruction d'une session Signal fraîche avant de chiffrer.
 */
export async function rewrapGroupKeyForParticipant(participantUserId, groupKeyRaw) {
  const address = new SignalProtocolAddress(String(participantUserId), DEVICE_ID);
  const addressStr = address.toString();

  // On efface tout état lié à l'ancienne identité de ce participant,
  // pour forcer une reconstruction complète avec sa nouvelle identité.
  await signalStore.remove(`session_${addressStr}`);
  await signalStore.remove(`identity_${addressStr}`);

  // wrapGroupKeyForParticipant reconstruira une session neuve,
  // puisque hasSession sera désormais false.
  return wrapGroupKeyForParticipant(participantUserId, groupKeyRaw);
}


const _repairRequested = new Set(); // discussionId déjà demandés cette session

export function requestGroupKeyRepair(discussionId, socket) {
  if (!socket || typeof socket.emit !== 'function') {
    console.warn('⚠️ requestGroupKeyRepair : socket indisponible, réparation non déclenchée', discussionId);
    return;
  }
  if (_repairRequested.has(discussionId)) return;
  _repairRequested.add(discussionId);
  socket.emit('request-group-key-repair', { discussionId });
}

export function resetGroupKeyState(discussionId) {
  _repairRequested.delete(discussionId);     // permet une nouvelle demande de réparation
  _groupKeyInFlight.delete(discussionId);    // abandonne un déchiffrement resté en attente
}

export async function getCachedGroupKey(discussionId) {
  try {
    const db = await openGroupKeyCacheDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(GROUP_KEY_CACHE_STORE, 'readonly');
      const req = tx.objectStore(GROUP_KEY_CACHE_STORE).get(discussionId);
      req.onsuccess = () => resolve(req.result); // Uint8Array (sérialisé) ou undefined
      req.onerror = () => reject(req.error);
    });
  } catch {
    return undefined;
  }
}

async function setCachedGroupKey(discussionId, groupKeyRaw) {
  try {
    const db = await openGroupKeyCacheDB();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(GROUP_KEY_CACHE_STORE, 'readwrite');
      tx.objectStore(GROUP_KEY_CACHE_STORE).put(groupKeyRaw, discussionId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.error('Erreur mise en cache de la clé de groupe:', err);
  }
}

const DEVICE_ID = 1;

// Convertit une string binaire Signal en base64 (byte par byte, sûr)
function binaryStringToBase64(str) {
  const bytes = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) {
    bytes[i] = str.charCodeAt(i) & 0xff;
  }
  return bufferToBase64(bytes);
}

// Convertit du base64 vers une string binaire Signal
function base64ToBinaryString(base64) {
  const bytes = base64ToBuffer(base64);
  let str = '';
  for (let i = 0; i < bytes.length; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return str;
}

/**
 * Génère une nouvelle clé AES-256 de groupe (pour une discussion privée).
 * Renvoie la CryptoKey (utilisable directement) + sa forme exportée en clair
 * (Uint8Array), pratique pour l'envelopper immédiatement après.
 */
export async function generateGroupKey() {
  const rawKey = crypto.getRandomValues(new Uint8Array(32));
  return rawKey; // Uint8Array — on ne garde que le format brut, ré-importé au besoin
}

export function generateMessageKey() {
  return crypto.getRandomValues(new Uint8Array(32));
}

export async function encryptGroupMessage(
  plaintext,
  members,
  currentUserId
) {
  if (typeof plaintext !== 'string') {
    throw new Error('encryptGroupMessage: plaintext doit être une string');
  }

  if (!Array.isArray(members)) {
    throw new Error('encryptGroupMessage: members doit être un tableau');
  }

  // 1. Clé AES unique pour CE message
  const messageKey = generateMessageKey();

  // 2. Chiffrement du contenu
  const {
    ciphertext,
    iv,
  } = await encryptTextWithKey(plaintext, messageKey);

  // 3. Enveloppes Signal pour les autres membres
  const keyForMembers = [];

  for (const member of members) {
    const memberId = member?._id || member;

    if (!memberId) continue;

    // Pas besoin de s'envoyer la clé via Signal
    if (String(memberId) === String(currentUserId)) {
      continue;
    }

    const wrapped = await encryptKeyForUser(
      memberId,
      messageKey
    );

    keyForMembers.push({
      userId: String(memberId),
      encryptedKey: wrapped.encryptedKey,
      keyType: wrapped.keyType,
    });
  }

  // 4. Copie pour l'expéditeur
  const selfEncrypted = await selfEncryptKey(messageKey);

  return {
    encryptedText: bufferToBase64(ciphertext),

    e2eeData: {
      textIv: bufferToBase64(iv),

      group: true,

      keyForMembers,

      keyForSender: bufferToBase64(
        selfEncrypted.encrypted
      ),

      keyForSenderIv: bufferToBase64(
        selfEncrypted.iv
      ),
    },
  };
}

async function encryptTextWithKey(plaintext, key) {
  const iv = crypto.getRandomValues(
    new Uint8Array(12)
  );

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    {
      name: 'AES-GCM',
      length: 256,
    },
    false,
    ['encrypt']
  );

  const encoded = new TextEncoder().encode(
    plaintext
  );

  const encrypted = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    cryptoKey,
    encoded
  );

  return {
    ciphertext: new Uint8Array(encrypted),
    iv,
  };
}

export async function decryptGroupMessage(message, currentUserId) {
  

  const e2eeData = message?.e2eeData;

  if (!e2eeData?.group) {
    console.error('❌ GROUP FLAG ABSENT', e2eeData);
    throw new Error(
      'Ce message ne contient pas de données E2EE groupe'
    );
  }

  const messageId = message?._id;
const cacheId = cacheKeyOf(message); 
  
  if (!messageId) {
    throw new Error(
      'Impossible de déchiffrer le message groupe : _id manquant'
    );
  }

  
let messageKey;

try {
  messageKey = await getCachedGroupMessageKey(cacheId);
  
} catch (err) {
  console.error('🔴 GROUP CACHE ERROR', err);
  throw err;
}

  if (!messageKey) {
    const senderId =
      message.sender?._id || message.sender;

    

    if (!senderId) {
      throw new Error(
        'Impossible de déchiffrer le message groupe : senderId manquant'
      );
    }
    if (String(senderId) === String(currentUserId)) {

      
      messageKey = await selfDecryptKey(
        base64ToBuffer(e2eeData.keyForSender),
        base64ToBuffer(e2eeData.keyForSenderIv)
      );

    } else {

   

      const myKey =
        e2eeData.keyForMembers?.find(
          item =>
            String(item.userId) ===
            String(currentUserId)
        );

      

      if (!myKey) {
        throw new Error(
          'Aucune clé de message pour cet utilisateur'
        );
      }

     

     
messageKey = await decryptKeyFromUser(
  myKey.encryptedKey,
  myKey.keyType,
  senderId
);


    }

    if (!messageKey || messageKey.byteLength !== 32) {
      throw new Error(
        `Clé AES groupe invalide : ${
          messageKey?.byteLength || 0
        } octets`
      );
    }

    await setCachedGroupMessageKey(
      cacheId,
      messageKey
    );
  }

  return await decryptTextWithKey(
    message.text,
    e2eeData.textIv,
    messageKey
  );
}
async function decryptTextWithKey(
  encryptedText,
  ivBase64,
  key
) {
  const cryptoKey =
    await crypto.subtle.importKey(
      'raw',
      key,
      {
        name: 'AES-GCM',
        length: 256,
      },
      false,
      ['decrypt']
    );

  const decrypted =
    await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: base64ToBuffer(ivBase64),
      },
      cryptoKey,
      base64ToBuffer(encryptedText)
    );

  return new TextDecoder().decode(decrypted);
}

/**
 * Chiffre un champ texte (titre ou description) avec la clé de groupe.
 * Retourne { ciphertext, iv } en base64, prêt à stocker en base.
 */
export async function encryptGroupField(plaintext, groupKeyRaw) {
  if (!plaintext) return null;
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    groupKeyRaw,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt']
  );
  const encoded = new TextEncoder().encode(plaintext);
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, cryptoKey, encoded);
  return {
    ciphertext: bufferToBase64(new Uint8Array(encrypted)),
    iv: bufferToBase64(iv),
  };
}

/**
 * Déchiffre un champ texte (titre ou description) avec la clé de groupe.
 * `field` = { ciphertext, iv } tel que stocké en base (en base64).
 */
export async function decryptGroupField(field, groupKeyRaw) {
  if (!field || !field.ciphertext || !field.iv) return '';
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    groupKeyRaw,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  );
  const ciphertext = base64ToBuffer(field.ciphertext);
  const iv = base64ToBuffer(field.iv);
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, ciphertext);
  return new TextDecoder().decode(decrypted);
}

/**
 * Enveloppe la clé de groupe pour SOI-MÊME (le créateur/propriétaire du wrap).
 * Utilise le même mécanisme que selfEncryptKey pour les messages (clé dérivée
 * de sa propre identité Signal), donc lisible sur n'importe lequel de ses appareils
 * partageant la même identité.
 */
export async function wrapGroupKeyForSelf(groupKeyRaw) {


// au déchiffrement : si e2eeData.keyForSelf existe, l'utiliser AVANT toute tentative Signal
  const { encrypted, iv } = await selfEncryptKey(groupKeyRaw);
  return {
    encryptedKey: bufferToBase64(encrypted),
    keyIv: bufferToBase64(iv),
    keyType: null, // null = wrap "self", pas un wrap Signal
    isSelf: true,
  };
}

/**
 * Déchiffre un wrap "self" pour récupérer la clé de groupe en clair.
 */
export async function unwrapGroupKeyForSelf(wrap) {
  const encryptedKey = base64ToBuffer(wrap.encryptedKey);
  const keyIv = base64ToBuffer(wrap.keyIv);
  const decrypted = await selfDecryptKey(encryptedKey, keyIv);
  return decrypted; // Uint8Array, la clé de groupe en clair
}

/**
 * Enveloppe la clé de groupe pour un AUTRE participant, via une session Signal
 * pairwise avec lui (construite si besoin). C'est l'inviteur qui appelle ceci,
 * au moment de l'invitation, pendant qu'il est en ligne.
 */
export async function wrapGroupKeyForParticipant(
  participantUserId,
  groupKeyRaw
) {
  const address = new SignalProtocolAddress(
    String(participantUserId),
    DEVICE_ID
  );

  const sessionKey = `session_${address.toString()}`;

  const hasSession = await signalStore.get(sessionKey);

  

  if (!hasSession) {
    

    await buildSessionWithContact(participantUserId);
  }

  return withSignalLock(address.toString(), async () => {
    const sessionRecord =
      await signalStore.loadSession(address.toString());

    const sessionCipher =
      new SessionCipher(signalStore, address);

    const signalEncrypted =
      await sessionCipher.encrypt(
        groupKeyRaw.buffer || groupKeyRaw
      );

    return {
      encryptedKey:
        binaryStringToBase64(signalEncrypted.body),
      keyIv: null,
      keyType: signalEncrypted.type,
      isSelf: false,
    };
  });
}
/**
 * Déchiffre un wrap Signal (reçu d'un autre participant) pour récupérer
 * la clé de groupe en clair. `authorUserId` = celui qui a créé ce wrap
 * (l'inviteur), nécessaire pour retrouver la bonne session Signal.
 */
export async function unwrapGroupKeyFromParticipant(wrap, authorUserId) {
  const address = new SignalProtocolAddress(authorUserId, DEVICE_ID);

  return withSignalLock(address.toString(), async () => {
    const sessionCipher = new SessionCipher(signalStore, address);
    const bodyBinary = base64ToBinaryString(wrap.encryptedKey);

    const keyBuffer = wrap.keyType === 3
      ? await sessionCipher.decryptPreKeyWhisperMessage(bodyBinary)
      : await sessionCipher.decryptWhisperMessage(bodyBinary);

    return new Uint8Array(keyBuffer);
  });
}

/**
 * Point d'entrée unique pour récupérer la clé de groupe en clair à partir
 * d'un wrap quelconque (self ou Signal). `wrap` doit inclure `isSelf` et,
 * si ce n'est pas un wrap self, `authorUserId` (qui a créé ce wrap — en
 * pratique le créateur de la discussion, ou l'inviteur).
 */
export async function unwrapGroupKey(wrap, authorUserId) {
  if (wrap.isSelf) {
    return unwrapGroupKeyForSelf(wrap);
  }
  return unwrapGroupKeyFromParticipant(wrap, authorUserId);
}

/**
 * Point d'entrée réutilisable : récupère la clé de groupe en clair d'une
 * discussion pour l'utilisateur courant — depuis le cache si possible, sinon
 * en déchiffrant le wrap (une seule fois, jamais rejoué).
 *
 * Utilisé aussi bien pour le titre/description de la discussion que pour
 * les événements d'agenda liés à cette discussion (même clé de groupe).
 *
 * Renvoie `null` si le wrap de l'utilisateur courant est introuvable
 * (rattrapage pas encore fait), pour que l'appelant décide comment l'afficher.
 */


const _groupKeyInFlight = new Map(); // discussionId -> Promise<Uint8Array|null>

export async function getGroupKeyForDiscussion(discussion, socket, currentUserId) {
  if (!discussion) {
    console.error('❌ GROUP KEY : discussion absente');
    return null;
  }

  if (discussion.isPublic) {
    //console.log('🔓 GROUP KEY : discussion publique');
    return null;
  }

  const wraps = discussion.groupKeyWraps || [];

  
  if (wraps.length === 0) {
    console.error(
      '❌ GROUP KEY : aucun groupKeyWrap pour cette discussion'
    );
    return null;
  }

  const myWrap = wraps.find(
    w => String(w.participantId) === String(currentUserId)
  );

  if (!myWrap) {
    console.error(
      '❌ GROUP KEY : aucun wrap trouvé pour cet utilisateur',
      {
        currentUserId: String(currentUserId),
        participantIds: wraps.map(w => String(w.participantId)),
      }
    );
    return null;
  }

  const discussionId = discussion._id;

  const cached = await getCachedGroupKey(discussionId);

  if (cached) {
    //console.log('✅ GROUP KEY : récupérée depuis le cache', discussionId);
    return cached;
  }

  if (_groupKeyInFlight.has(discussionId)) {
    
    return _groupKeyInFlight.get(discussionId);
  }

  const resolvePromise = (async () => {
    try {
        const authorId = myWrap.authorId || discussion.creator?._id || discussion.creator;

      
      const groupKeyRaw = await unwrapGroupKey(
        myWrap,
        authorId
      );

      if (!groupKeyRaw) {
        throw new Error(
          'unwrapGroupKey() a retourné une clé vide'
        );
      }

      
      await setCachedGroupKey(
        discussionId,
        groupKeyRaw
      );

if (!myWrap.isSelf) {
  await persistSelfWrap(discussionId, groupKeyRaw); 
}
      return groupKeyRaw;

    } catch (err) {
      console.error('❌ GROUP KEY : échec unwrapGroupKey', { discussionId, error: err });
  requestGroupKeyRepair(discussionId, socket); 
  throw err;

    } finally {
      _groupKeyInFlight.delete(discussionId);
    }
  })();

  _groupKeyInFlight.set(
    discussionId,
    resolvePromise
  );

  return resolvePromise;
}
/**
 * Point d'entrée unique pour l'affichage : à partir d'un objet `discussion`
 * tel que renvoyé par l'API (getDiscussionById / getUserDiscussions), renvoie
 * { title, description } en clair.
 *
 * - Discussion PUBLIQUE : le serveur a déjà déchiffré, on renvoie tel quel.
 * - Discussion PRIVÉE : on retrouve le wrap de l'utilisateur courant, on le
 *   déchiffre (self ou Signal selon le cas), on obtient la clé de groupe,
 *   puis on déchiffre title/description avec.
 *
 * En cas d'échec (wrap absent — rattrapage pas encore fait par l'inviteur,
 * ou erreur quelconque), renvoie un libellé de repli plutôt que de lever.
 */
export async function decryptDiscussionFields(discussion, socket, currentUserId) {
  if (!discussion) return { title: '', description: '' };

  if (discussion.isPublic) {
    return {
      title: discussion.title || '',
      description: discussion.description || '',
    };
  }

  try {
    // LEGACY — discussion privée créée avant la migration E2EE : le serveur
    // a déjà déchiffré title/description lui-même (voir fallback côté API),
    // ils arrivent donc comme de simples strings, pas des { ciphertext, iv }.
    const noGroupKey = !discussion.groupKeyWraps || discussion.groupKeyWraps.length === 0;
    if (noGroupKey) {
      return {
        title: typeof discussion.title === 'string' ? discussion.title : '',
        description: typeof discussion.description === 'string' ? discussion.description : '',
      };
    }

    const myWrap = discussion.groupKeyWraps.find(
      w => String(w.participantId) === String(currentUserId)
    );
    if (!myWrap) {
      return { title: '[Titre en attente de synchronisation]', description: '' };
    }

    const groupKeyRaw = await getGroupKeyForDiscussion(discussion, socket, currentUserId);
    if (!groupKeyRaw) {
      return { title: '[Titre en attente de synchronisation]', description: '' };
    }

    const title = discussion.title
      ? await decryptGroupField(discussion.title, groupKeyRaw)
      : '';
    const description = discussion.description
      ? await decryptGroupField(discussion.description, groupKeyRaw)
      : '';

    return { title, description };
  } catch (err) {
    console.error('Erreur déchiffrement titre/description de discussion:', err);
    return { title: '[Titre indéchiffrable]', description: '' };
  }
}

const _sessionResetRequested = new Map(); // senderId -> timestamp

export function requestSessionReset(socket, senderId, err) {
  if (!senderId || senderId === 'undefined') return;
  const msg = String(err?.message || '');
  if (!/No record for device|Bad MAC|No session|Invalid prekey id/i.test(msg)) return;
  if (!socket || typeof socket.emit !== 'function') return;

  const last = _sessionResetRequested.get(senderId);
  if (last && Date.now() - last < 60_000) return; // une demande par minute et par expéditeur
  _sessionResetRequested.set(senderId, Date.now());
  socket.emit('request-session-reset', { targetUserId: senderId });
}

export function clearSessionResetRequest(senderId) {
  _sessionResetRequested.delete(String(senderId));
}
/**
 * Déchiffre le titre et la description d'un événement d'agenda, avec la clé
 * de groupe de la discussion à laquelle il appartient. `groupKeyRaw` doit
 * avoir été récupéré au préalable via getGroupKeyForDiscussion (une seule
 * fois pour toute la session d'affichage de l'agenda, pas par événement).
 */
export async function decryptEventFields(event, groupKeyRaw) {
  if (!event) return { title: '', description: '' };
  if (!groupKeyRaw) {
    return { title: '[Indéchiffrable]', description: '' };
  }
  try {
    const title = event.title
      ? await decryptGroupField(event.title, groupKeyRaw)
      : '';
    const description = event.description
      ? await decryptGroupField(event.description, groupKeyRaw)
      : '';
    return { title, description };
  } catch (err) {
    console.error('Erreur déchiffrement titre/description d\'événement:', err);
    return { title: '[Indéchiffrable]', description: '' };
  }
}

/**
 * Chiffre le titre et la description d'un événement avant envoi au serveur,
 * avec la clé de groupe de la discussion. Renvoie { title, description }
 * au format { ciphertext, iv } (description peut être null si vide).
 */
export async function encryptEventFields(title, description, groupKeyRaw) {
  const encryptedTitle = await encryptGroupField(title || '', groupKeyRaw);
  const encryptedDescription = description
    ? await encryptGroupField(description, groupKeyRaw)
    : null;
  return { title: encryptedTitle, description: encryptedDescription };
}




